import type { Attestation } from '@/types/attestation'
import type { Badge } from '@/types/common'
import type { Creature, NurtureEvent } from '@/types/creature'
import type { GameResult, GameSession } from '@/types/game'
import type { CareEvent, Plant } from '@/types/plant'
import type { Player } from '@/types/player'
import Dexie, { type Table } from 'dexie'

export interface BadgeRecord {
  id: string
  playerId: string
  badge: Badge
  awardedAt: string
}

export interface EventLogRecord {
  id: string
  kind: string
  payload: Record<string, unknown>
  timestamp: string
}

export interface CreatureAssetRecord {
  id: string
  creatureId: string
  kind: 'thumbnail' | 'model'
  blob: Blob
  createdAt: string
}

export interface CreatureAssetPinRecord {
  id: string
  creatureId: string
  variant: 'preview' | 'full'
  url: string
  bytes?: number
  pinnedAt: string
  updatedAt: string
}

export interface AtprotoAccountRecord {
  id: string
  playerId: string
  did: string
  handle: string
  accessJwt: string
  refreshJwt: string
  expiresAt: string
  createdAt: string
  updatedAt: string
}

export interface ProofRefRecord {
  id: string
  playerId: string
  did?: string
  schemaUid: string
  attestationUid: string
  kind: string
  createdAt: string
}

export interface UnlockMembershipRecord {
  id: string
  playerId: string
  address: string
  lockAddress: string
  chainId: number
  hasValidKey: boolean
  keyExpiration: number | null
  checkedAt: string
  source: 'network' | 'cache' | 'fallback'
}

export type UGCModerationStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'flagged'
  | 'canon-candidate'
  | 'canonized'

export type UGCKind = 'creature-pack' | 'shrine-challenge' | 'game-variant' | 'skin-collection'

export interface UGCRecord {
  id: string
  playerId: string
  kind: UGCKind
  title: string
  contentHash: string
  contentUri?: string
  lineage: string[]
  moderationStatus: UGCModerationStatus
  policyLabels: string[]
  createdByAgent: boolean
  agentLabel?: string
  attestationUid?: string
  createdAt: string
  updatedAt: string
}

export interface WorldRegistryRecord {
  id: string
  season: string
  shrineId: string
  ugcId: string
  ugcKind?: UGCKind
  contentHash: string
  canonAttestationUid: string
  listingId?: number
  tokenId?: number
  recordedAt: string
}

export type SkinListingStatus = 'listed' | 'sold-out' | 'delisted'

export interface SkinListingRecord {
  id: string
  ugcId: string
  playerId: string
  contentHash: string
  metadataUri?: string
  unitPriceWei: string
  maxSupply: number
  soldSupply: number
  status: SkinListingStatus
  contractListingId?: number
  tokenId?: number
  createdAt: string
  updatedAt: string
}

export interface OwnedSkinRecord {
  id: string
  playerId: string
  listingId: string
  ugcId: string
  quantity: number
  tokenId?: number
  acquiredAt: string
  updatedAt: string
}

export interface GreenGoodsReportRecord {
  id: string
  careEventId: string
  playerId: string
  status: 'queued' | 'submitted' | 'failed'
  reportId?: string
  error?: string
  createdAt: string
  updatedAt: string
}

export function createId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }

  return `wefa_${Date.now()}_${Math.floor(Math.random() * 1_000_000)}`
}

export function nowIso(): string {
  return new Date().toISOString()
}

export class WEFADatabase extends Dexie {
  players!: Table<Player>
  plants!: Table<Plant>
  careEvents!: Table<CareEvent>
  creatures!: Table<Creature>
  nurtureEvents!: Table<NurtureEvent>
  gameSessions!: Table<GameSession>
  gameResults!: Table<GameResult>
  badges!: Table<BadgeRecord>
  attestations!: Table<Attestation>
  eventLog!: Table<EventLogRecord>
  creatureAssets!: Table<CreatureAssetRecord>
  creatureAssetPins!: Table<CreatureAssetPinRecord>
  atprotoAccounts!: Table<AtprotoAccountRecord>
  proofRefs!: Table<ProofRefRecord>
  unlockMemberships!: Table<UnlockMembershipRecord>
  ugcItems!: Table<UGCRecord>
  worldRegistry!: Table<WorldRegistryRecord>
  skinListings!: Table<SkinListingRecord>
  ownedSkins!: Table<OwnedSkinRecord>
  greenGoodsReports!: Table<GreenGoodsReportRecord>

  constructor() {
    super('wefa-db')
    this.version(6).stores({
      players: 'id, name, energy, onboardingComplete, createdAt, updatedAt',
      plants: 'id, playerId, element, biome, createdAt, updatedAt, careStreak',
      careEvents: 'id, plantId, playerId, timestamp, synced',
      creatures: 'id, playerId, element, stage, createdAt, updatedAt, xp',
      nurtureEvents: 'id, creatureId, playerId, timestamp, synced',
      gameSessions: 'id, hostId, guestId, status, type, joinCode, createdAt, updatedAt',
      gameResults: 'id, playerId, gameType, sessionId, finishedAt',
      badges: 'id, playerId, badge, awardedAt',
      attestations: 'id, schemaId, attester, recipient, timestamp',
      eventLog: 'id, kind, timestamp',
      creatureAssets: 'id, creatureId, kind, createdAt',
      creatureAssetPins: 'id, creatureId, variant, url, updatedAt',
      atprotoAccounts: 'id, playerId, did, handle, updatedAt',
      proofRefs: 'id, playerId, attestationUid, kind, createdAt',
      unlockMemberships: 'id, playerId, address, lockAddress, chainId, checkedAt',
      ugcItems: 'id, playerId, kind, moderationStatus, contentHash, updatedAt',
      worldRegistry: 'id, season, shrineId, ugcKind, contentHash, listingId, tokenId, recordedAt',
      skinListings:
        'id, ugcId, playerId, contentHash, status, contractListingId, tokenId, updatedAt',
      ownedSkins: 'id, playerId, listingId, ugcId, tokenId, updatedAt',
      greenGoodsReports: 'id, careEventId, playerId, status, updatedAt',
    })
  }
}

export const db = new WEFADatabase()

export async function getPrimaryPlayer(): Promise<Player | null> {
  const player = await db.players.orderBy('createdAt').first()
  return player ?? null
}

export async function ensurePrimaryPlayer(
  partial?: Partial<Pick<Player, 'name' | 'authMode' | 'address'>>,
): Promise<Player> {
  const existing = await getPrimaryPlayer()
  if (existing) {
    if (partial && (partial.name || partial.authMode || partial.address)) {
      const updates: Partial<Player> = {
        ...(partial.name ? { name: partial.name } : {}),
        ...(partial.authMode ? { authMode: partial.authMode } : {}),
        ...(partial.address ? { address: partial.address } : {}),
        updatedAt: nowIso(),
      }
      await db.players.update(existing.id, updates)
      return { ...existing, ...updates }
    }

    return existing
  }

  const now = nowIso()
  const player: Player = {
    id: createId(),
    name: partial?.name ?? 'Keeper',
    authMode: partial?.authMode ?? 'local',
    address: partial?.address,
    energy: 12,
    onboardingComplete: false,
    badges: [],
    createdAt: now,
    updatedAt: now,
  }

  await db.players.add(player)
  return player
}
